// =============================================================================
// MILESTONES SERVICE - Firebase CRUD for Milestones
// =============================================================================
//
// ⚓ ANCHORED: 2026-01-27
// Deze service bevat kritieke fixes voor Firestore undefined value errors.
// NIET WIJZIGEN zonder documentatie te raadplegen:
// → PROJECT_NOTES/DOELSTELLINGEN_FIXES_2026-01-27.md
//
// Belangrijke functies:
// - removeUndefinedFields(): VERPLICHT voor alle Firestore writes
// - createMilestone(): Gebruikt removeUndefinedFields voor data cleaning
// - updateMilestone(): Gebruikt removeUndefinedFields voor data cleaning
// =============================================================================

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { Milestone } from '../types';

const COLLECTION_NAME = 'milestones';

// -----------------------------------------------------------------------------
// HELPER: Remove undefined values (Firestore doesn't accept undefined)
// -----------------------------------------------------------------------------

function removeUndefinedFields<T extends object>(obj: T): T {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result as T;
}

// -----------------------------------------------------------------------------
// CREATE
// -----------------------------------------------------------------------------

/**
 * Create a new milestone in Firestore
 * @param ownerId - Firebase Auth UID
 * @param goalId - Parent goal ID
 * @param data - Milestone data (without id, createdAt, ownerId, goalId)
 * @returns The created milestone's ID
 */
export async function createMilestone(
  ownerId: string,
  goalId: string,
  data: Omit<Milestone, 'id' | 'createdAt' | 'ownerId' | 'goalId'>
): Promise<string> {
  const milestoneData: Omit<Milestone, 'id'> = {
    ...data,
    ownerId,
    goalId,
    createdAt: Date.now(),
  };

  // Remove undefined fields before saving to Firestore
  const cleanData = removeUndefinedFields(milestoneData);

  const docRef = await addDoc(collection(db, COLLECTION_NAME), cleanData);
  
  // Update the document with its own ID for consistency
  await updateDoc(docRef, { id: docRef.id });
  
  return docRef.id;
}

// -----------------------------------------------------------------------------
// READ
// -----------------------------------------------------------------------------

/**
 * Get all milestones for a specific goal
 * @param ownerId - Firebase Auth UID
 * @param goalId - Parent goal ID
 * @returns Array of Milestone objects
 */
export async function getMilestonesByGoal(
  ownerId: string,
  goalId: string
): Promise<Milestone[]> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId)
  );

  const snapshot = await getDocs(q);
  
  // Sort in memory
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Milestone))
    .sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Get all milestones for a user (across all goals)
 * @param ownerId - Firebase Auth UID
 * @returns Array of Milestone objects
 */
export async function getAllMilestones(ownerId: string): Promise<Milestone[]> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId)
  );

  const snapshot = await getDocs(q);
  
  // Sort in memory
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Milestone))
    .sort((a, b) => a.createdAt - b.createdAt);
}

// -----------------------------------------------------------------------------
// UPDATE
// -----------------------------------------------------------------------------

/**
 * Update an existing milestone
 * @param milestoneId - The milestone's document ID
 * @param data - Partial milestone data to update
 */
export async function updateMilestone(
  milestoneId: string,
  data: Partial<Omit<Milestone, 'id' | 'createdAt' | 'ownerId' | 'goalId'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, milestoneId);
  // Remove undefined fields before updating Firestore
  const cleanData = removeUndefinedFields(data);
  await updateDoc(docRef, cleanData);
}

// -----------------------------------------------------------------------------
// DELETE (with cascade)
// -----------------------------------------------------------------------------

/**
 * Delete a milestone and all associated actions
 * @param milestoneId - The milestone's document ID
 */
export async function deleteMilestone(milestoneId: string): Promise<void> {
  // 1. Delete all actions for this milestone
  const actionsQuery = query(
    collection(db, 'actions'),
    where('milestoneId', '==', milestoneId)
  );
  const actionsSnapshot = await getDocs(actionsQuery);
  
  const actionDeletePromises = actionsSnapshot.docs.map(doc => 
    deleteDoc(doc.ref)
  );
  await Promise.all(actionDeletePromises);

  // 2. Delete the milestone itself
  const milestoneRef = doc(db, COLLECTION_NAME, milestoneId);
  await deleteDoc(milestoneRef);
}
